/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { getTelemetryObject, IModuleProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IAccountGenericTileConfig, IAccountGenericTileProps } from './account-generic-tile.props.autogenerated';
import LinksComponent from './components/account-generic-tile-links';

export interface IAccountGenericTileViewProps extends IAccountGenericTileProps<IAccountGenericTileConfig> {
    AccountGenericTile: IModuleProps;
    className: string;
    heading?: React.ReactNode;
    links?: React.ReactNode;
}

/**
 *
 * AccountGeneric component.
 * @extends {React.PureComponent<IAccountGenericTileProps<IAccountGenericTileConfig>>}
 */
class AccountGenericTile extends React.PureComponent<IAccountGenericTileProps<IAccountGenericTileConfig>> {
    private readonly telemetryContent: ITelemetryContent;

    constructor(props: IAccountGenericTileProps<IAccountGenericTileConfig>) {
        super(props);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public render(): JSX.Element {
        const { config } = this.props;
        const { className, heading, links } = config;

        const viewProps = {
            ...this.props,
            className,
            AccountGenericTile: {
                moduleProps: this.props,
                className: classnames('ms-account-generic-tile', config.className)
            },
            heading: heading && heading.text && (
                <Msdyn365.Text
                    className='ms-account-generic-tile__heading'
                    tag={heading.tag || 'h2'}
                    text={heading.text}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            links: links && links.length > 0 && (
                <LinksComponent
                    {...{
                        links,
                        onTextChange: this.handleLinkTextChange,
                        requestContext: this.props.context.request,
                        telemetryContent: this.telemetryContent
                    }}
                />
            )
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.heading!.text = event.target.value);

    public handleLinkTextChange = (linkIndex: number) => (event: Msdyn365.ContentEditableEvent) => {
        if (this.props.config.links && this.props.config.links[linkIndex]) {
            this.props.config.links[linkIndex].linkText = event.target.value;
        }
    };
}

export default AccountGenericTile;
